"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, ArrowRight, Sparkles, HeartHandshake } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const projectsData = [{
  month: "SEPTEMBER 2026",
  title: "Community Solar & Light Drive",
  description: "Join Q4 Initiative",
  status: "Completed",
  category: "Outreach",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/community_solar_drive.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/community_solar_drive.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/community_solar_drive.png-wvc-sizes",
  alt: "Volunteers distributing solar light kits in the community"
}, {
  month: "OCTOBER 2026",
  title: "Youth Mentorship Workshops",
  description: "Join Q4 Initiative",
  status: "Upcoming",
  category: "Education",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/youth_mentorship_workshop.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/youth_mentorship_workshop.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/youth_mentorship_workshop.png-wvc-sizes",
  alt: "Children participating in an artistic mentorship workshop"
}];
export default function Timeline() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.2
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="currentprojectstimeline" data-nav="light" className="bg-background text-foreground py-20 md:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {}
        <div className="text-center flex flex-col items-center max-w-3xl mx-auto mb-16 md:mb-24">
          <Badge variant="secondary" className="rounded-lg px-4 py-1.5 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-secondary/30 mb-6 shadow-xs">
            <Sparkles className="size-3.5 mr-1" />
            Our Work
          </Badge>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-foreground tracking-tight leading-tight mb-6">
            Making the world a{" "}
            <span className="italic font-serif text-secondary-foreground underline decoration-secondary decoration-wavy decoration-2 underline-offset-6">
              better place
            </span>
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground font-default max-w-2xl leading-relaxed">Guided by our motto of <strong className="text-foreground font-semibold">Love and Light</strong>, Victoria Light Foundation is executing targeted community initiatives across the 4th quarter.</p>
        </div>

        {}
        <div className="relative">
          {}
          <div aria-hidden="true" className="hidden lg:block absolute top-[4.5rem] left-1/4 right-1/4 h-1 bg-border z-0">
            <div className="h-full bg-secondary/80 w-1/2" />
          </div>

          {}
          <div aria-hidden="true" className="lg:hidden absolute top-8 bottom-8 left-6 w-1 bg-border z-0">
            <div className="w-full bg-secondary/80 h-1/2" />
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 relative z-10 max-w-4xl mx-auto">
            {projectsData.map((project, i) => <motion.div key={project.title} data-index={i} variants={itemVariants} className="flex flex-col pl-12 lg:pl-0">
                {}
                <div className="absolute left-3 lg:left-1/2 lg:-translate-x-1/2 -translate-y-1/2 top-8 lg:top-[4.5rem] flex items-center justify-center">
                  <div className="size-7 rounded-lg bg-background border-4 border-secondary flex items-center justify-center shadow-sm">
                    <div className="size-2 rounded-lg bg-primary" />
                  </div>
                </div>

                {}
                <Card className="bg-card text-card-foreground border border-border shadow-md rounded-lg overflow-hidden hover:shadow-xl transition-[box-shadow,border-color] duration-300 flex flex-col h-full mt-0 lg:mt-12">
                  <div className="relative h-48 overflow-hidden bg-muted">
                    <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-primary text-primary-foreground font-mono text-[11px] px-2.5 py-1 rounded-lg shadow-xs">
                        {project.category}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pb-3 pt-6">
                    <div className="flex items-center gap-2 text-xs font-mono font-bold text-secondary-foreground mb-2">
                      <SafeHydrate><Calendar className="size-3.5 text-secondary-foreground" /></SafeHydrate>
                      <span>{project.month}</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground font-sans font-medium">{project.status}</span>
                    </div>
                    <CardTitle className="text-xl font-serif font-bold text-foreground leading-snug">
                      {project.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between pb-6">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {project.description}
                    </CardDescription>

                    <div>
                      <Button asChild variant="outline" size="sm" className="w-full rounded-lg border-border hover:bg-accent hover:text-accent-foreground group">
                        <Link to="/QuarterMembershipPage">
                          <span>Join Q3 Initiative</span>
                          <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {}
        
      </div>
    </section>;
}